import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import React from 'react';
import styled from 'styled-components';
import { getTabMenuAnchor } from './tabMenuAnchorUtil';

interface Iprops {
    showAllTabs: boolean;
    onToggleShowAllTabs: () => void;
    isHistoricalMode: boolean;
    hasNotInUseTabs: boolean;
    t: TFunction;
}

const MenuWrapper = styled.div`
    flex-shrink: 0;
    user-select: none;
`;

/*
 * Flex-centred rather than relying on the button's default inline layout: FontAwesome gives
 * .svg-inline--fa.fa-lg a vertical-align of -0.225em (-3.6px here), which drops the glyph below the
 * baseline and leaves it visibly low in the tab row. vertical-align does not apply to a flex item,
 * so centring the icon this way removes the offset instead of compensating for it.
 */
const EllipsisButton = styled.button`
    align-items: center;
    background: transparent;
    border: none;
    color: #007bc3;
    cursor: pointer;
    display: flex;
    font-size: 16px;
    height: 41px;
    justify-content: center;
    min-height: 41px;
    padding: 0 14px;

    &:hover {
        background-color: #fff;
    }

    &:focus {
        outline: 2px solid #007bc3;
        outline-offset: -2px;
    }
`;

const Menu = styled.div<{ menuTop: number; menuRight: number }>`
    background-color: #fff;
    position: fixed;
    top: ${(props: { menuTop: number; menuRight: number }) => props.menuTop}px;
    right: ${(props: { menuTop: number; menuRight: number }) => props.menuRight}px;
    min-width: 200px;
    z-index: 999;
    border: 1px solid #f1f1f1;
    box-shadow: 0px 5px 6px rgba(0, 0, 0, 0.25);
    border-radius: 0 0 4px 4px;
    display: flex;
    flex-direction: column;
    /*
     * Symmetric, unlike TabSelector's 4px/8px menu this is otherwise modelled on. That menu lists many
     * tabs, where the imbalance is invisible; this one holds a single item, which would sit 2px high.
     */
    padding: 4px 0;
`;

const Item = styled.div`
    align-items: center;
    color: #007bc3;
    cursor: pointer;
    display: flex;
    font-size: 15px;
    gap: 8px;
    line-height: 1.3;
    padding: 10px 12px;
    width: 100%;
    word-break: break-word;

    &:hover {
        background-color: #c1edff;
    }
`;

/*
 * Height matches the item's line box (1.3 x its font size) so the checkmark occupies the same box as
 * the label beside it and the two centre together, rather than the slot collapsing to the icon's own
 * height and drifting against the text.
 */
const CheckSlot = styled.span`
    align-items: center;
    display: inline-flex;
    height: 1.3em;
    justify-content: center;
    width: 14px;
`;

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100dvh;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 997;
`;

/*
 * Overflow menu for the measurement point tab bar (MMG-4405). Today it holds a single item, Show All
 * Tabs, which reveals tabs and In Use dropdowns hidden by the In Use gate. Shown only when at least one
 * tab is Not In Use, so an asset with everything in use (or a first visit, where no gate is answered
 * yet) shows every tab and no ellipsis.
 *
 * The rule lives here rather than at the call sites, including historical mode: nothing is hidden on a
 * read-only past inspection, so the item would take a checkmark and do nothing. Mobile and desktop both
 * render this component, and a rule duplicated in two JSX branches rots.
 */
export const MeasurementPointTabMenu = (props: Iprops) => {
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [menuTop, setMenuTop] = React.useState<number>(0);
    const [menuRight, setMenuRight] = React.useState<number>(0);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const handleToggle = () => {
        if (!isOpen && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const anchor = getTabMenuAnchor(rect, document.documentElement.clientWidth);
            setMenuTop(anchor.top);
            setMenuRight(anchor.right);
        }
        setIsOpen(prev => !prev);
    };

    const handleSelect = () => {
        props.onToggleShowAllTabs();
        setIsOpen(false);
    };

    // Close on scroll of any ancestor, resize, orientation change, or Escape - a fixed
    // menu positioned once on open would otherwise strand itself (mirrors DatetimePortal.tsx)
    React.useEffect(() => {
        if (!isOpen) return;

        const close = () => setIsOpen(false);
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };

        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        window.addEventListener('orientationchange', close);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
            window.removeEventListener('orientationchange', close);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    if (props.isHistoricalMode || !props.hasNotInUseTabs) {
        return null;
    }

    return (
        <MenuWrapper ref={wrapperRef} className="measurement-point-tab-menu">
            {isOpen && <Backdrop onClick={() => setIsOpen(false)} />}
            <EllipsisButton
                type="button"
                onClick={handleToggle}
                aria-label={String(props.t('measurementPoints:tabOptions'))}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                <FontAwesomeIcon icon="ellipsis-h" size="lg" />
            </EllipsisButton>
            {isOpen && (
                <Menu menuTop={menuTop} menuRight={menuRight} role="menu">
                    <Item
                        role="menuitem"
                        tabIndex={0}
                        onClick={handleSelect}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSelect();
                            }
                        }}
                    >
                        <CheckSlot>{props.showAllTabs && <FontAwesomeIcon icon={['far', 'check']} />}</CheckSlot>
                        {String(props.t('measurementPoints:showAllTabs'))}
                    </Item>
                </Menu>
            )}
        </MenuWrapper>
    );
};

export default MeasurementPointTabMenu;
